import { useEffect, useRef } from 'react';
import { LoaderCircle } from 'lucide-react';

/**
 * Fetches the next page when you actually reach the bottom.
 *
 * IntersectionObserver rather than a scroll listener: no per-frame arithmetic, no
 * throttling to tune, and it is correct inside a nested scroller — which is what a
 * board column is.
 *
 * rootMargin is ZERO. It used to be 400px, which on a phone is most of a screen: the
 * next page arrived while you were still reading the middle of the current one, so the
 * column silently grew under you and it was impossible to tell when a fetch was
 * happening or why. Firing at the bottom means the request is always a consequence of
 * something you just did.
 */
export function LoadMoreSentinel({
  onVisible,
  loading,
  remaining,
}: {
  onVisible: () => void;
  loading: boolean;
  remaining: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Held in a ref so the observer is not torn down and rebuilt every render — a
  // re-subscribe mid-scroll can miss the intersection entirely.
  const handler = useRef(onVisible);
  handler.current = onVisible;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) handler.current();
      },
      { rootMargin: '0px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    /*
     * ONE fixed height, whatever state it is in.
     *
     * It used to swap "12 more" for two skeleton cards, so the moment the fetch started
     * the footer grew by ~140px and shoved everything above it — and then shrank again
     * when the real cards replaced it. The list appeared to shake twice per page. A box
     * that never changes size means the arriving cards are inserted ABOVE a fixed point
     * and nothing already on screen moves.
     */
    <div
      ref={ref}
      className="flex h-12 shrink-0 items-center justify-center gap-2 text-[11px]"
      style={{ color: 'var(--text-faint)' }}
      aria-live="polite"
    >
      {loading ? (
        <>
          <LoaderCircle size={14} className="animate-spin" aria-hidden />
          <span className="sr-only">Loading more tasks</span>
        </>
      ) : (
        `${remaining} more`
      )}
    </div>
  );
}
